import type { NavItem } from '@/app/nav/collect'
import type { BadgeKey } from '@/app/stores/counts'

/** A page of the menu: a top-level entry, or a group's child with the group it belongs to. */
export interface NavEntry {
  item: NavItem
  parent?: NavItem
}

export interface NavAlert {
  entry: NavEntry
  count: number
}

/** Every page of the menu in menu order: the entries without children, and each group's children. */
export function flattenNav(items: NavItem[]): NavEntry[] {
  const entries: NavEntry[] = []
  for (const item of items) {
    if (item.children?.length) {
      for (const child of item.children) entries.push({ item: child, parent: item })
    } else {
      entries.push({ item })
    }
  }
  return entries
}

/**
 * The pages whose badge is lit, for the navbar's alerts tray: the trash count is housekeeping, not something waiting
 * on the user, and a group's own badge is not listed (Finances' `finGlobal` is the sum of its two children's badges,
 * which are listed instead).
 */
export function alertEntries(items: NavItem[], badge: (key: BadgeKey) => number): NavAlert[] {
  const alerts: NavAlert[] = []
  for (const entry of flattenNav(items)) {
    const key = entry.item.badge
    if (!key || key === 'trashCount') continue
    const count = badge(key)
    if (count > 0) alerts.push({ entry, count })
  }
  return alerts
}

/** Lower case without diacritics, so "depenses" finds "Dépenses". */
export function foldText(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
}

/** The entries whose label (or their group's) contains every word of `query`; all of them for an empty query. */
export function searchNav(entries: NavEntry[], label: (item: NavItem) => string, query: string): NavEntry[] {
  const words = foldText(query).split(/\s+/).filter(Boolean)
  if (!words.length) return entries
  return entries.filter((entry) => {
    const text = foldText([entry.parent ? label(entry.parent) : '', label(entry.item)].join(' '))
    return words.every((word) => text.includes(word))
  })
}
